import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Button,
  SelectChangeEvent,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  debounce,
} from "@mui/material";
import axios from "axios";
import to from "await-to-js";
import { useNavigate } from "react-router-dom";
import "froala-editor/css/froala_editor.pkgd.min.css";
import { axiosWithAuth } from "../plugins/axios";
import {
  DataGridPro,
  GridSortModel,
  GridFilterModel,
} from "@mui/x-data-grid-pro";
import { AddConfigurationModal } from "../modal/AddNewConfigurationModal";
import { CloneSolutionModal } from "../modal/CloneSolutionModal";
import { quizColumns } from "../components/QuizColumns";
interface Assets {
  _id: number;
  name: string;
  usages: any;
  workshopDetails: any;
}

interface Category {
  id: string;
  name: string;
}

function LxpAssests() {
  const [assets, setAssets] = useState<Assets[]>([]);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [quizzes, setQuizzes] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingQuizId, setEditingQuizId] = useState<number | null>(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success"
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(50);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  // Modal states for cohort configuration
  const [cohortModalOpen, setCohortModalOpen] = useState(false);
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [formData, setFormData] = useState({
    cohortId: "",
    quizId: "",
    problemStatement: "",
    categoryId: "",
    teachUrl: "",
  });
  const [formErrors, setFormErrors] = useState({
    cohortId: false,
    quizId: false,
    problemStatement: false,
    categoryId: false,
    teachUrl: false,
  });
  const [cloneFormData, setCloneFormData] = useState({
    cloneFromCohortId: "",
    cloneFromQuizId: "",
    cloneToCohortId: "",
    cloneToQuizId: "",
    teachUrl: "",
  });
  const [cloneFormErrors, setCloneFormErrors] = useState({
    cloneFromCohortId: false,
    cloneFromQuizId: false,
    cloneToCohortId: false,
    cloneToQuizId: false,
    teachUrl: false,
  });
  const [filterModel, setFilterModel] = useState<GridFilterModel>();

  const apiUrl = process.env.REACT_APP_PIPELINE_API_LXP;
  const navigate = useNavigate();

  const debounceFetchAssets = useMemo(
    () =>
      debounce((filters) => {
        fetchAssets(filters);
      }, 600),
    [activeTab]
  );

  const nestedColumnValues: { [key: string]: string } = {
    assetName: "name",
    programName: "usages.programName",
    cohortId: "workshopDetails.code",
  };
  const handleFilterChange = useCallback(
    ({ items }: GridFilterModel) => {
      const currentTab = activeTab;
      if (!items.length) {
        setFilterModel({ items: [] });
        debounceFetchAssets([]);
        return;
      }

      const filters = items.map((item) => ({
        column: nestedColumnValues[item.field] || item.field,
        value: item.value,
        operator: item.operator,
        activeTab: currentTab,
      }));

      setFilterModel({ items });

      debounceFetchAssets(filters);
    },
    [debounceFetchAssets, nestedColumnValues, activeTab]
  );

  const fetchAssets = async (filters?: any[]) => {
    const apiEndPoint =
      activeTab === 1
        ? `${apiUrl}cohortconfiguration/assets/project`
        : `${apiUrl}cohortconfiguration/assets/assignment`;

    const sortField = sortModel[0]?.field;
    const mappedSortColumn = sortField
      ? nestedColumnValues[sortField] || sortField
      : null;

    const params = {
      page: page + 1,
      limit: rowsPerPage,
      sortColumn: mappedSortColumn,
      sortDirection: sortModel[0]?.sort || null,
      ...(filters && filters.length > 0
        ? { filters: JSON.stringify(filters) }
        : {}),
    };

    const [err, response] = await to(
      axiosWithAuth(
        apiEndPoint,
        {
          params,
        },
        "get"
      )
    );

    if (err) {
      setError("Failed to load Assests");
    } else {
      const { data, totalCount } = response.data;
      setAssets(data);
      setTotalCount(totalCount);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!filterModel || filterModel.items.length === 0) {
      fetchAssets();
    }
  }, [apiUrl, activeTab, page, rowsPerPage, sortModel, filterModel]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setPage(0);
    setFilterModel({ items: [] });
    setSortModel([]);
  };

  const handleCohortModalOpen = () => setCohortModalOpen(true);
  const handleCohortModalClose = () => {
    setCohortModalOpen(false);
    setFormErrors({
      cohortId: false,
      quizId: false,
      problemStatement: false,
      categoryId: false,
      teachUrl: false,
    });
  };

  const handleCloneModalClose = () => {
    setCloneModalOpen(false);
    setCloneFormErrors({
      cloneFromCohortId: false,
      cloneFromQuizId: false,
      cloneToCohortId: false,
      cloneToQuizId: false,
      teachUrl: false,
    });
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleCloneFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCloneFormData((prev) => ({ ...prev, [name]: value }));
    setCloneFormErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    setFormData((prev) => ({ ...prev, categoryId: event.target.value }));
    setFormErrors((prev) => ({ ...prev, categoryId: false }));
    setFormData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  const handleFroalaChange = (content: string) => {
    setFormData((prev) => ({ ...prev, problemStatement: content }));
    setFormErrors((prev) => ({ ...prev, problemStatement: false }));
  };

  const handleCohortFormSubmit = async () => {
    const { cohortId, quizId, problemStatement, categoryId, teachUrl } =
      formData;
    const errors = {
      cohortId: cohortId.trim() === "",
      quizId: quizId.trim() === "",
      problemStatement: problemStatement.trim() === "",
      categoryId: categoryId === "",
      teachUrl: teachUrl === "",
    };
    setFormErrors(errors);
    if (Object.values(errors).some((error) => error)) return;

    setIsSubmitting(true);
    const [submitErr] = await to(
      axiosWithAuth(
        `${apiUrl}cohortconfiguration/create`,
        {
          ...formData,
          submissionType: Number(categoryId),
          settings: { submissionType: Number(categoryId) },
        },
        "post"
      )
    );
    if (submitErr) {
      const errorMessage =
        axios.isAxiosError(submitErr) && submitErr.response?.data?.message
          ? submitErr.response?.data?.message
          : "Failed to create Assets";
      setToastMessage(errorMessage);
      setToastSeverity("error");
      setToastOpen(true);
    } else {
      handleCohortModalClose();
      setFormData({
        cohortId: "",
        quizId: "",
        problemStatement: "",
        categoryId: "",
        teachUrl: "",
      });
      fetchAssets();
    }
    setIsSubmitting(false);
  };

  const handleCloneFormSubmit = async () => {
    const {
      cloneFromCohortId,
      cloneFromQuizId,
      cloneToCohortId,
      cloneToQuizId,
      teachUrl,
    } = cloneFormData;
    const errors = {
      cloneFromCohortId: cloneFromCohortId.trim() === "",
      cloneFromQuizId: cloneFromQuizId.trim() === "",
      cloneToCohortId: cloneToCohortId.trim() === "",
      cloneToQuizId: cloneToQuizId.trim() === "",
      teachUrl: teachUrl.trim() === "",
    };
    setCloneFormErrors(errors);
    if (Object.values(errors).some((error) => error)) return;

    setIsSubmitting(true);
    const [submitErr] = await to(
      axiosWithAuth(
        `${apiUrl}cohortconfiguration/cloneSolution/create`,
        {
          cloneFrom: { cohortId: cloneFromCohortId, quizId: cloneFromQuizId },
          cloneTo: { cohortId: cloneToCohortId, quizId: cloneToQuizId },
          teachUrl,
        },
        "post"
      )
    );
    if (submitErr) {
      const errorMessage =
        axios.isAxiosError(submitErr) && submitErr.response?.data?.message
          ? submitErr.response.data.message
          : "Failed to clone solution";
      setToastMessage(errorMessage);
      setToastSeverity("error");
      setToastOpen(true);
    } else {
      handleCloneModalClose();
      setCloneFormData({
        cloneFromCohortId: "",
        cloneFromQuizId: "",
        cloneToCohortId: "",
        cloneToQuizId: "",
        teachUrl: "",
      });
      fetchAssets();
    }
    setIsSubmitting(false);
  };

  const validateHierarchy = (node: any, level: number) => {
    if (level === 1 && (!node.children || node.children.length === 0)) {
      throw new Error(
        `Validation Error: Level 1 node "${node.name}" has no Level 2 children.`
      );
    }
    if (level === 2 && (!node.children || node.children.length === 0)) {
      throw new Error(
        `Validation Error: Level 2 node "${node.name}" has no Level 3 children.`
      );
    }
    if (node.children) {
      node.children.forEach((child: any) =>
        validateHierarchy(child, level + 1)
      );
    }
  };

  const validateChildMarks = (node: any) => {
    if (node.children && node.children.length > 0) {
      const childrenSum = node.children.reduce(
        (sum: number, child: any) => sum + child.maxMarks,
        0
      );
      if (node.maxMarks !== childrenSum) {
        throw new Error(
          `Validation Error: Max marks of node "${node.name}" (ID: ${node.id}) is ${node.maxMarks}, which does not match the sum of its children's marks (${childrenSum}).`
        );
      }
      node.children.forEach(validateChildMarks);
    }
  };
  const handleToastClose = () => setToastOpen(false);

  const columns = quizColumns({
    assets,
    activeTab,
    navigate,
  }) as readonly any[];

  if (loading && assets.length === 0) {
    return (
      <Box mt={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  return (
    <Box mt={4}>
      {/* <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button onClick={handleCohortModalOpen} variant="outlined">
          Add New Configuration
        </Button>
      </Box> */}

      <Typography variant="h4" gutterBottom>
        Cohorts
      </Typography>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="cohort tabs"
      >
        {["Assignments", "Projects"].map((name, index) => (
          <Tab key={index} label={name} sx={{ fontWeight: "bold" }} />
        ))}
      </Tabs>

      <Box mt={3}>
        {loading ? (
          <Box display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <DataGridPro
            rows={assets}
            columns={columns}
            paginationMode="server"
            filterMode="server"
            sortingMode="server"
            rowCount={totalCount}
            paginationModel={{ page, pageSize: rowsPerPage }}
            onPaginationModelChange={(model) => {
              setPage(model.page);
              setRowsPerPage(model.pageSize);
            }}
            pageSizeOptions={[5, 10, 20, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row._id}
            autoHeight
            onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
            onFilterModelChange={handleFilterChange}
          />
        )}
      </Box>

      <AddConfigurationModal
        open={cohortModalOpen}
        onClose={handleCohortModalClose}
        formData={formData}
        formErrors={formErrors}
        categories={categories}
        onFormChange={handleFormChange}
        onSelectChange={handleSelectChange}
        onFroalaChange={handleFroalaChange}
        onSubmit={handleCohortFormSubmit}
        isSubmitting={isSubmitting}
      />

      <CloneSolutionModal
        open={cloneModalOpen}
        onClose={handleCloneModalClose}
        cloneFormData={cloneFormData}
        cloneFormErrors={cloneFormErrors}
        onFormChange={handleCloneFormChange}
        onSubmit={handleCloneFormSubmit}
        isSubmitting={isSubmitting}
      />
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={handleToastClose}
      >
        <Alert
          onClose={handleToastClose}
          severity={toastSeverity}
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default LxpAssests;
